import IMask from 'imask';
import { applyPhoneMask, getPhoneState } from '@/js/autorization/phone.utils';

(() => {
  const global = window;

  const ConfValidationUrls = globalConf(globalConfValidationUrls);
  const ConfValidation = global.ConfValidation;
  if (!ConfValidation) return;

  /* ================= helpers ================= */

  const qs = (s, r = document) => r.querySelector(s);
  const qsa = (s, r = document) => Array.from(r.querySelectorAll(s));

  const show = (el) => el && (el.style.display = '');
  const hide = (el) => el && (el.style.display = 'none');

  const setText = (el, t) => el && (el.textContent = t ?? '');

  const setAriaInvalid = (el, s) => el?.setAttribute?.('aria-invalid', !!s);

  const closestInputGroup = (el) => el?.closest?.('.input-group') || null;
  const setGroupError = (el, s) => closestInputGroup(el)?.classList.toggle('error', !!s);

  const addErr = (el, c) => el?.classList.add(c);
  const removeErr = (el, c) => el?.classList.remove(c);

  const setDisabled = (el, s) => {
    if (!el) return;
    el.disabled = !!s;
    s ? el.setAttribute('disabled', 'disabled') : el.removeAttribute('disabled');
  };

  const debounce = (fn, d = 400) => {
    let t;
    return (...a) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(null, a), d);
    };
  };

  /* ================= async helpers ================= */

  const ajaxGetJson = async (url, params = {}) => {
    const u = new URL(url, location.origin);
    Object.entries(params).forEach(([k, v]) => u.searchParams.set(k, v ?? ''));
    const r = await fetch(u, { credentials: 'same-origin' });
    return r.json();
  };

  const ajaxPostJson = async (url, body = {}) => {
    const r = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return r.json();
  };

  const recaptchaExec = (action) =>
    new Promise((resolve, reject) => {
      if (!global.grecaptcha || !ConfValidation.recaptcha_key_v3) {
        reject(new Error('reCAPTCHA v3 not available'));
        return;
      }

      global.grecaptcha.ready(() => {
        global.grecaptcha
          .execute(ConfValidation.recaptcha_key_v3, { action })
          .then(resolve)
          .catch(reject);
      });
    });

  const validateCyrillicName = ({ inputEl, errorEl, errClass, requiredMsg, wrongLangMsg, minLen, minLenMsg }) => {
    const re = /[^а-щьюяыъґєіїА-ЩЬЮЯЫЪҐЄІЇ'’` -]/;

    const value = (inputEl?.value || '').trim();

    hide(errorEl);
    removeErr(inputEl, errClass);
    setGroupError(inputEl, false);
    setAriaInvalid(inputEl, false);

    if (!value) {
      setText(errorEl, requiredMsg);
      show(errorEl);
      addErr(inputEl, errClass);
      setGroupError(inputEl, true);
      setAriaInvalid(inputEl, true);
      return false;
    }

    if (re.test(value)) {
      setText(errorEl, wrongLangMsg);
      show(errorEl);
      addErr(inputEl, errClass);
      setGroupError(inputEl, true);
      setAriaInvalid(inputEl, true);
      return false;
    }

    if (minLen && value.length < minLen) {
      setText(errorEl, minLenMsg);
      show(errorEl);
      addErr(inputEl, errClass);
      setGroupError(inputEl, true);
      setAriaInvalid(inputEl, true);
      return false;
    }

    return true;
  };

  /* ================= main ================= */

  const C = {
    errClass: 'input--error',

    _phoneChecking: false,
    _emailChecking: false,

    init() {
      this.form = qs(ConfValidation.formId);
      if (!this.form) return;

      this.firstName = qs(ConfValidation.firstNameId);
      this.lastName = qs(ConfValidation.lastNameId);
      this.email = qs(ConfValidation.email);
      this.phone = qs(ConfValidation.phoneId);
      this.birthdate = qs(ConfValidation.birthdateId);
      this.birthdateText = qs('#date_birthday_text');

      this.firstNameError = qs(ConfValidation.firstNameError);
      this.lastNameError = qs(ConfValidation.lastNameError);
      this.emailError = qs(ConfValidation.emailError);
      this.phoneError = qs(ConfValidation.phoneError);
      this.birthdateErr = qs(ConfValidation.errorFieldId);

      this.agree = qs('#customer_agreement');
      this.agreeError = qs('#agreement-err');

      this.submitBtn = this.form.querySelector('[type="submit"]');

      // ✅ phone mask (shared util)
      this.phoneMask = this.phone ? applyPhoneMask(this.phone) : null;

      // dynamic checks (phone/email async)
      this.email?.addEventListener('input', debounce(() => this._checkEmail()));
      this.phone?.addEventListener('input', debounce(() => this._checkPhone()));
      if(sessionStorage.getItem('reg_phone')) {
        this.phone.value = sessionStorage.getItem('reg_phone'); 
        
      }
      // dynamic checks (name/birthdate sync)
      this.firstName?.addEventListener('input', debounce(() => this._validateFirstName()));
      this.firstName?.addEventListener('blur', () => this._validateFirstName());

      this.lastName?.addEventListener('input', debounce(() => this._validateLastName()));
      this.lastName?.addEventListener('blur', () => this._validateLastName());

      this.birthdate?.addEventListener('change', () => this._validateBirthdate());
      this.birthdate?.addEventListener('blur', () => this._validateBirthdate());

      this.form.addEventListener('submit', (e) => this._onSubmit(e));

      this._initMasks(); // date masks
      this._initAgreementToggle();
    },

    _initAgreementToggle() {
      if (!this.agree || !this.submitBtn) return;
      const toggle = () => {
        this.submitBtn.disabled = !this.agree.checked;
        this.submitBtn.style.opacity = this.agree.checked ? '1' : '0.5';
      };
      toggle();
      this.agree.addEventListener('change', toggle);
    },

    _initMasks() {
      const locale = '{{ shop.locale }}';
      const placeholder = locale === 'ru' ? 'дд/мм/гггг' : 'дд/мм/рррр';

      qsa('.date-mask').forEach((i) => IMask(i, { mask: '00/00/0000', placeholder }));

      const text = qs('#date_birthday_text');
      const hidden = qs('#date_birthday');

      if (text && hidden) {
        IMask(text, { mask: '00/00/0000', placeholder });

        text.addEventListener('input', () => {
          const value = text.value;

          if (value.length < 10) {
            hide(this.birthdateErr);
            removeErr(hidden, this.errClass);
            setGroupError(hidden, false);
            setAriaInvalid(text, false);
            return;
          }

          const [d, m, y] = value.split('/');
          hidden.value = `${y}-${m}-${d}`;

          this._validateBirthdate();
        });
      }
    },

    /* ---------- async checks ---------- */

    async _checkPhone() {
      if (!this._validatePhone() || this._phoneChecking) return;
      this._phoneChecking = true;

      try {
        hide(this.phoneError);
        removeErr(this.phone, this.errClass);
        setGroupError(this.phone, false);
        setAriaInvalid(this.phone, false);

        const action = 'customer_reg_ver_phone';
        const token = await recaptchaExec(action);

        const r = await ajaxGetJson(ConfValidationUrls.urlValidatePhone, {
          phone: this.phone.value,
          rec_token: token,
          rec_action: action,
        });

        const ok = !!r?.data?.success;
        if (!ok) {
          const msg = r?.data?.msg || r?.message || ConfValidation.msg.wrongPhone;
          this._setPhoneError(msg);
        }
      } catch (e) {
        console.error('Phone server validation error', e);
        this._setPhoneError(ConfValidation.msg.tryAgain || ConfValidation.msg.wrongPhone);
      } finally {
        this._phoneChecking = false;
      }
    },

    async _checkEmail() {
      if (!this._validateEmail() || this._emailChecking) return;
      this._emailChecking = true;

      try {
        hide(this.emailError);
        removeErr(this.email, this.errClass);
        setGroupError(this.email, false);
        setAriaInvalid(this.email, false);

        const action = 'customer_reg_email';
        const token = await recaptchaExec(action);

        const r = await ajaxGetJson(ConfValidationUrls.urlValidateEmail, {
          email: this.email.value,
          rec_token: token,
          rec_action: action,
        });

        const ok = !!r?.data?.success;
        if (!ok) {
          const msg = r?.data?.msg || r?.message || ConfValidation.msg.wrongEmail;
          this._setEmailError(msg);
        }
      } catch (e) {
        console.error('Email server validation error', e);
        this._setEmailError(ConfValidation.msg.tryAgain || ConfValidation.msg.wrongEmail);
      } finally {
        this._emailChecking = false;
      }
    },

    /* ---------- submit ---------- */

    async _onSubmit(e) {
      e.preventDefault();

      if (this._phoneChecking || this._emailChecking) return;

      const valid =
        this._validateFirstName() &
        this._validateLastName() &
        this._validateEmail() &
        this._validatePhone() &
        this._validateBirthdate() &
        this._validateAgree();

      if (!valid) {
        const firstErrorInput = this.form.querySelector('.input--error, [aria-invalid="true"]');
        if (firstErrorInput) {
          const errorId = firstErrorInput.getAttribute('aria-errormessage');
          const errorMsg = errorId ? qs(`#${errorId}`) : null;
          if (errorMsg) {
            errorMsg.focus();
          } else {
            firstErrorInput.focus();
          }
        }
        return;
      }

      this._submitForm();
    },

    async _submitForm() {
      setDisabled(this.submitBtn, true);
    
      try {
        const token = await recaptchaExec('customer_register');
    
        const payload = {
          first_name: this.firstName?.value || '',
          last_name: this.lastName?.value || '',
          phone: this.phone?.value || '',
          email: this.email?.value || '',
          date_birthday: (this.birthdate?.value || '').split('-').reverse().join('/'),
          customer_agreement: '1',
          rec_token: token,
          rec_action: 'customer_register',
        };
    
        const r = await ajaxPostJson(ConfValidationUrls.urlLoginPhone, payload);
    
        if (r?.data?.customerId) {
          const { rec_token, ...sessionData } = payload;

					if (typeof window.EsTracking !== 'undefined') {
						try {
							window.EsTracking.onRegister({
								id: r?.data?.customerId,
								name: (this.firstName?.value || '') + ' ' + (this.lastName?.value || ''),
								phone: this.phone?.value || '',
								email: this.email?.value || ''
							});
						} catch (e) {
							console.error('EsTracking', e);
						}
					}
    
          sessionStorage.setItem(
            'user_register_data',
            JSON.stringify(sessionData)
          );
    
          sessionStorage.setItem(
            'user_register_phone',
            payload.phone
          );
    
          location.href = ConfValidationUrls.urlConfirmPhone.replace(
            '{id}',
            r.data.customerId
          );
        }
      } finally {
        setDisabled(this.submitBtn, false);
      }
    },
    

    /* ---------- validators ---------- */

    _validateBirthdate() {
      hide(this.birthdateErr);
      removeErr(this.birthdate, this.errClass);
      setGroupError(this.birthdate, false);
      setAriaInvalid(this.birthdateText, false);

      const v = this.birthdate?.value;
      if (!v) return this._birthErr(ConfValidation.msg.required);

      const [y, m, d] = v.split('-').map(Number);
      const date = new Date(y, m - 1, d);

      if (
        !date ||
        Number.isNaN(date.getTime()) ||
        date.getFullYear() !== y ||
        date.getMonth() !== m - 1 ||
        date.getDate() !== d
      ) return this._birthErr(ConfValidation.msg.wrongDate);

      const today = new Date();
      const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());

      let age = todayDate.getFullYear() - date.getFullYear();
      const monthDiff = todayDate.getMonth() - date.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && todayDate.getDate() < date.getDate())) age--;

      if (age < 18) return this._birthErr(ConfValidation.msg.tooYoung);
      if (age > 99) return this._birthErr(ConfValidation.msg.wrongDate);

      return true;
    },

    _birthErr(msg) {
      setText(this.birthdateErr, msg);
      show(this.birthdateErr);
      addErr(this.birthdate, this.errClass);
      setGroupError(this.birthdate, true);
      setAriaInvalid(this.birthdateText, true);
      return false;
    },

    _validateEmail() {
      const re = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+$/;
      const value = (this.email?.value || '').trim();

      hide(this.emailError);
      removeErr(this.email, this.errClass);
      setGroupError(this.email, false);
      setAriaInvalid(this.email, false);

      if (!value) return this._setEmailErrorAndFalse(ConfValidation.msg.required);
      if (!re.test(value)) return this._setEmailErrorAndFalse(ConfValidation.msg.wrongEmail);

      return true;
    },

    _setEmailErrorAndFalse(msg) {
      this._setEmailError(msg);
      return false;
    },

    _setEmailError(msg) {
      setText(this.emailError, msg);
      show(this.emailError);
      addErr(this.email, this.errClass);
      setGroupError(this.email, true);
      setAriaInvalid(this.email, true);
    },

    _validatePhone() {
      const value = (this.phone?.value || '').trim();

      hide(this.phoneError);
      removeErr(this.phone, this.errClass);
      setGroupError(this.phone, false);
      setAriaInvalid(this.phone, false);

      if (!value) {
        this._setPhoneError(ConfValidation.msg.required);
        return false;
      }

      const st = getPhoneState(value, this.phoneMask);

      if (!st.isComplete || !st.formatOk || !st.operatorOk) {
        this._setPhoneError(ConfValidation.msg.wrongPhone);
        return false;
      }

      return true;
    },

    _setPhoneError(msg) {
      setText(this.phoneError, msg);
      show(this.phoneError);
      addErr(this.phone, this.errClass);
      setGroupError(this.phone, true);
      setAriaInvalid(this.phone, true);
    },

    _validateAgree() {
      if (!this.agree?.checked) {
        show(this.agreeError);
        setText(this.agreeError, ConfValidation.msg.required);
        setAriaInvalid(this.agree, true);
        return false;
      }
      hide(this.agreeError);
      setAriaInvalid(this.agree, false);
      return true;
    },

    _validateFirstName() {
      return validateCyrillicName({
        inputEl: this.firstName,
        errorEl: this.firstNameError,
        errClass: this.errClass,
        requiredMsg: ConfValidation.msg.required,
        wrongLangMsg: ConfValidation.msg.wrongLang,
        minLen: 2,
        minLenMsg: ConfValidation.msg.nameLengthError,
      });
    },

    _validateLastName() {
      return validateCyrillicName({
        inputEl: this.lastName,
        errorEl: this.lastNameError,
        errClass: this.errClass,
        requiredMsg: ConfValidation.msg.required,
        wrongLangMsg: ConfValidation.msg.wrongLang,
        minLen: 3,
        minLenMsg: ConfValidation.msg.lastNameLengthError,
      });
    },
  };

  document.addEventListener('DOMContentLoaded', () => C.init());
})();
